import React, { useState } from 'react';
import {
  Printer,
  Download,
  X,
  CheckCircle2,
  Award,
  FileText,
  ShieldCheck,
  CreditCard,
  User,
  Calendar,
  Clock,
  MapPin,
  Building2
} from 'lucide-react';
import {
  logoDataPreschoolKindergarten,
  logoDataPrimary,
  logoDataSecondary
} from '../../assets/logo/logoDataUrls';
import logoPreschoolKindergarten from '../../assets/logo/logo_preschool_kindergarten.png';
import logoPrimary from '../../assets/logo/logo_primary.png';
import logoSecondary from '../../assets/logo/logo_secondary.png';
import './EnglishAdmissionsDocuments.css';

function getLevelBranding(lvlCode, gradeTarget) {
  const code = String(lvlCode || '').toLowerCase();
  const grade = String(gradeTarget || '').toLowerCase();

  const isEarlyYears = code.includes('preschool') || code.includes('kindergarten') || code.includes('kg') || code.includes('toddler') || code.includes('nursery') || code.includes('tk') || grade.includes('kga') || grade.includes('kgb') || grade.includes('preschool') || grade.includes('kindergarten') || grade.includes('tk') || grade.includes('nursery') || grade.includes('toddler');
  const isPrimary = code.includes('primary') || code.includes('sd') || grade.includes('primary') || grade.includes('sd') || grade.includes('grade 1') || grade.includes('grade 2') || grade.includes('grade 3') || grade.includes('grade 4') || grade.includes('grade 5') || grade.includes('grade 6') || grade.includes('kelas 1') || grade.includes('kelas 2') || grade.includes('kelas 3') || grade.includes('kelas 4') || grade.includes('kelas 5') || grade.includes('kelas 6');

  if (isEarlyYears) {
    return {
      logo: logoDataPreschoolKindergarten || logoPreschoolKindergarten,
      publicFallback: '/logo_preschool_kindergarten.png',
      logoClass: 'ead-logo-wide',
      boxClass: 'ead-logo-box-wide',
      foundation: 'YAYASAN PENDIDIKAN PERMATA HARAPAN',
      schoolName: 'KINDERFIELD HIGHFIELD SCHOOL SIMPRUG',
      subSchoolName: 'PRESCHOOL & KINDERGARTEN - SIMPRUG',
      directorate: 'EARLY CHILDHOOD EDUCATION & ADMISSIONS DIRECTORATE',
      sealText: 'KINDERFIELD\nSIMPRUG\nJAKARTA',
      levelType: 'early_childhood',
      directorName: 'Ratna Sari, S.Pd., M.Pd'
    };
  }

  if (isPrimary) {
    return {
      logo: logoDataPrimary || logoPrimary,
      publicFallback: '/logo_primary.png',
      logoClass: 'ead-logo-square',
      boxClass: 'ead-logo-box-square',
      foundation: 'YAYASAN PENDIDIKAN PERMATA HARAPAN',
      schoolName: 'SEKOLAH PERMATA HARAPAN',
      subSchoolName: 'PRIMARY EDUCATION (SD)',
      directorate: 'PRIMARY ADMISSIONS & ACADEMIC ENROLLMENT DIRECTORATE',
      sealText: 'SEKOLAH\nPERMATA\nHARAPAN',
      levelType: 'primary',
      directorName: 'Asnah Roidah Yulianti, S.Si., M.Pd'
    };
  }

  return {
    logo: logoDataSecondary || logoSecondary,
    publicFallback: '/logo_secondary.png',
    logoClass: 'ead-logo-tall',
    boxClass: 'ead-logo-box-tall',
    foundation: 'YAYASAN PENDIDIKAN PERMATA HARAPAN',
    schoolName: 'PERMATA HARAPAN SECONDARY',
    subSchoolName: 'JUNIOR & SENIOR HIGH SCHOOL',
    directorate: 'SECONDARY ADMISSIONS & ACADEMIC ENROLLMENT DIRECTORATE',
    sealText: 'PERMATA\nHARAPAN\nSECONDARY',
    levelType: 'secondary',
    directorName: 'Asnah Roidah Yulianti, S.Si., M.Pd'
  };
}

function formatFormalDate(dateStr) {
  if (!dateStr) return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (_) {
    return dateStr;
  }
}

function formatCurrencyIDR(amount) {
  const num = Number(amount || 0);
  return 'IDR ' + num.toLocaleString('en-US');
}

/**
 * 1. OFFICIAL LETTER OF ACCEPTANCE (LoA)
 * Formal English Admission Acceptance Letter
 */
export function LetterOfAcceptanceModal({ app, onClose }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  if (!app) return null;

  const childName = app.child_full_name || app.student_name || app.name || 'Student Candidate';
  const regNo = app.registration_no || `REG-PHS-${app.id || '0001'}`;
  const loaNumber = app.loa_number || `LOA-PHS/2026/${String(app.id || '0001').padStart(4, '0')}`;
  const issuedDate = formatFormalDate(app.loa_issued_at || app.updated_at);
  const academicYear = app.academic_year || '2026/2027';
  const gradeTarget = app.grade_target || app.level_name || 'Primary School';
  const campus = app.campus_location || 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13';

  const formData = app.form_data || {};
  const parentInfo = app.parent_info || {};

  // Child detailed particulars
  const birthPlace = app.birth_place || formData.birth_place || formData.place_of_birth || '';
  const rawBirthDate = app.birth_date || formData.birth_date || formData.date_of_birth || '';
  const birthDateFormatted = rawBirthDate ? formatFormalDate(rawBirthDate) : '';
  const birthPlaceDate = [birthPlace, birthDateFormatted].filter(Boolean).join(', ') || '-';

  const genderRaw = app.gender || formData.gender || formData.jenis_kelamin || '';
  const genderLower = String(genderRaw).toLowerCase();
  const gender = (genderLower === 'male' || genderLower === 'l' || genderLower === 'laki-laki') ? 'Male'
    : (genderLower === 'female' || genderLower === 'p' || genderLower === 'perempuan') ? 'Female'
    : (genderRaw || '-');

  const rawNat = app.nationality || formData.nationality || formData.kewarganegaraan || 'Indonesian';
  const nationality = (String(rawNat).toLowerCase().includes('wni') || String(rawNat).toLowerCase().includes('indonesia')) ? 'Indonesian (WNI)' : rawNat;
  const religion = app.religion || formData.religion || formData.agama || '-';
  const previousSchool = app.previous_school || formData.previous_school || formData.asal_sekolah || '-';

  // Parent / Guardian detailed particulars
  const parentName = parentInfo.full_name || app.applicant_name || formData.parent_name || 'Parent / Guardian';
  const fatherName = parentInfo.father_name || formData.father_name || formData.nama_ayah || '-';
  const motherName = parentInfo.mother_name || formData.mother_name || formData.nama_ibu || '-';
  const parentPhone = parentInfo.phone || app.applicant_phone || formData.phone || formData.whatsapp || '-';
  const parentEmail = parentInfo.email || app.applicant_email || app.email || formData.email || '-';
  const parentAddress = parentInfo.home_address || formData.home_address || formData.address || formData.alamat || '-';
  const parentOccupation = formData.father_occupation || formData.mother_occupation || formData.parent_job || formData.pekerjaan || '-';

  // Level Branding & Signatories
  const branding = getLevelBranding(app.level_code || app.level || app.level_name, gradeTarget);
  const officerName = app.admissions_officer_name || 'Ivana';
  const officerRole = 'Admissions Officer';
  const directorName = app.school_director_name || app.school_principal_name || branding.directorName;
  const directorRole = 'School Director';

  // Complete Fee Components without exception
  const inv = app.invoice || {};
  const lvlCfg = app.level_config || {};

  const regFee = Number(inv.registration_fee ?? lvlCfg.registration_fee ?? 250000);
  const testFee = Number(inv.test_reg_form_fee ?? lvlCfg.test_reg_form_fee ?? 250000);
  const admissionFee = Number(inv.admission_fee ?? lvlCfg.admission_fee ?? 1500000);
  const devFee = Number(inv.development_fee ?? lvlCfg.development_fee ?? 12000000);

  const df1 = Number(inv.df_1_amount ?? lvlCfg.df_1_amount ?? (devFee * 0.4));
  const df2 = Number(inv.df_2_amount ?? lvlCfg.df_2_amount ?? (devFee * 0.3));
  const df3 = Number(inv.df_3_amount ?? lvlCfg.df_3_amount ?? (devFee * 0.15));
  const df4 = Number(inv.df_4_amount ?? lvlCfg.df_4_amount ?? (devFee * 0.15));

  const booksFee = Number(inv.books_activity_fee ?? lvlCfg.books_activity_fee ?? 2000000);
  const tuitionFee = Number(inv.tuition_fee ?? lvlCfg.tuition_fee ?? 1800000);

  const discountAmount = Number(inv.discount_amount ?? app.discount_amount ?? 0);
  const discountLabel = inv.discount_label || app.promo_code_used || 'Approved Incentive';

  const subtotalFees = regFee + testFee + admissionFee + devFee + booksFee + tuitionFee;
  const totalPayable = Number(inv.total_payable || (subtotalFees - discountAmount));

  const bankName = inv.bank_name || 'Bank Central Asia (BCA)';
  const bankAccount = inv.bank_account_number || '253 388 9988';
  const bankHolder = inv.bank_account_holder || 'Yayasan Permata Harapan Bangsa';

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Letter_of_Acceptance_${childName.replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownloadPdf = async () => {
    const page1El = document.querySelector('.ead-loa-page-1');
    const page2El = document.querySelector('.ead-loa-page-2');
    if (!page1El || !page2El) return;
    try {
      setDownloadingPdf(true);
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      const canvasOpts = {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        logging: false,
        backgroundColor: '#ffffff'
      };

      const canvas1 = await html2canvas(page1El, canvasOpts);
      const canvas2 = await html2canvas(page2El, canvasOpts);

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pageWidth = 210;
      const pageHeight = 297;

      const imgHeight1 = (canvas1.height * pageWidth) / canvas1.width;
      pdf.addImage(canvas1.toDataURL('image/jpeg', 0.98), 'JPEG', 0, 0, pageWidth, Math.min(imgHeight1, pageHeight), undefined, 'FAST');

      pdf.addPage('a4', 'portrait');
      const imgHeight2 = (canvas2.height * pageWidth) / canvas2.width;
      pdf.addImage(canvas2.toDataURL('image/jpeg', 0.98), 'JPEG', 0, 0, pageWidth, Math.min(imgHeight2, pageHeight), undefined, 'FAST');

      pdf.save(`Letter_of_Acceptance_${childName.replace(/\s+/g, '_')}.pdf`);
    } catch (err) {
      console.error('PDF generation error, fallback to print:', err);
      handlePrint();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="ead-modal-overlay" onClick={onClose}>
      <div className="ead-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ead-modal-toolbar">
          <div className="ead-toolbar-title">
            <Award size={18} />
            <span>Letter of Acceptance (LoA) - {childName}</span>
          </div>
          <div className="ead-toolbar-actions">
            <button
              type="button"
              className="ead-btn-download"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <Download size={15} />
              <span>{downloadingPdf ? 'Saving PDF...' : 'Download Official PDF (.pdf)'}</span>
            </button>
            <button type="button" className="ead-btn-print" onClick={handlePrint}>
              <Printer size={15} />
              <span>Print Document</span>
            </button>
            <button type="button" className="ead-btn-close" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ead-paper-scroll">
          <div className="ead-loa-pdf-export-container">
            {/* ===================================================================
                PAGE 1 (PAGE 1 OF 2): OFFICIAL LETTER OF ACCEPTANCE & PARTICULARS
            =================================================================== */}
            <div className="ead-sheet ead-sheet-loa-page ead-loa-page-1">
              <div>
                {/* 1. ELEGANT EXECUTIVE LETTERHEAD */}
                <div className="ead-exec-header">
                  <div className="ead-exec-brand">
                    <div className="ead-exec-logo-wrap">
                      <img
                        src={branding.logo}
                        alt={branding.schoolName}
                        className={`ead-kop-logo ${branding.logoClass}`}
                        onError={(e) => {
                          if (branding.publicFallback && e.currentTarget.src !== branding.publicFallback) {
                            e.currentTarget.src = branding.publicFallback;
                          }
                        }}
                      />
                    </div>
                    <div className="ead-exec-titles">
                      <span className="ead-exec-foundation">{branding.foundation}</span>
                      <span className="ead-exec-school">{branding.schoolName}</span>
                      <span className="ead-exec-subschool">{branding.subSchoolName}</span>
                      <span className="ead-exec-directorate">{branding.directorate}</span>
                    </div>
                  </div>
                  <div className="ead-exec-header-contact">
                    <strong>Admissions &amp; Enrollment Directorate</strong>
                    Jl. Pos Pengumben Lama No. 13, Jakarta 11630<br />
                    Phone: (021) 5890 1100 &bull; WhatsApp: 0895-4045-01010<br />
                    admissions@permataharapansecondary.sch.id
                  </div>
                </div>

                {/* 2. DOCUMENT METADATA STRIP */}
                <div className="ead-exec-meta-bar">
                  <div>Reference No: <strong>{loaNumber}</strong></div>
                  <div>Date of Issuance: <strong>{issuedDate}</strong></div>
                </div>

                {/* 3. DOCUMENT TITLE */}
                <div className="ead-exec-title-block">
                  <h1 className="ead-exec-title">OFFICIAL LETTER OF ACCEPTANCE</h1>
                  <div className="ead-exec-subtitle">FORMAL ADMISSION OFFER &bull; ACADEMIC YEAR {academicYear}</div>
                </div>

                {/* 4. RECIPIENT & SALUTATION */}
                <div className="ead-exec-recipient">
                  <div>To the Parents / Guardians of: <strong>{childName}</strong></div>
                  <div>Application ID: <strong>{regNo}</strong></div>
                </div>

                <div className="ead-exec-salutation">Dear Parents / Guardians,</div>

                <p className="ead-exec-paragraph">
                  Following the comprehensive evaluation of the entrance assessment, academic readiness observation, and parental interview, the Admissions Committee together with the School Directorate are pleased to inform you that your child, <strong>{childName}</strong>, has successfully met all institutional standards and has been <strong>OFFICIALLY ACCEPTED</strong> for enrollment at <strong>{branding.schoolName}</strong> for Academic Year <strong>{academicYear}</strong>.
                </p>

                {/* 5. PARTICULARS: CLEAN DEFINITION GRID (NO HEAVY BOXES!) */}
                <div className="ead-exec-section">
                  <div className="ead-exec-section-header">
                    <User size={13} />
                    <span>1. Particulars of Accepted Student</span>
                  </div>
                  <div className="ead-exec-grid">
                    <div className="ead-exec-field">
                      <span className="ead-exec-label">Full Name</span>
                      <span className="ead-exec-val">{childName}</span>
                    </div>
                    <div className="ead-exec-field">
                      <span className="ead-exec-label">Registration ID</span>
                      <span className="ead-exec-val">{regNo}</span>
                    </div>
                    <div className="ead-exec-field">
                      <span className="ead-exec-label">Accepted Grade &amp; Level</span>
                      <span className="ead-exec-val">{gradeTarget} ({branding.schoolName})</span>
                    </div>
                    <div className="ead-exec-field">
                      <span className="ead-exec-label">Academic Year &amp; Campus</span>
                      <span className="ead-exec-val">AY {academicYear} &bull; {campus}</span>
                    </div>
                    <div className="ead-exec-field">
                      <span className="ead-exec-label">Place &amp; Date of Birth</span>
                      <span className="ead-exec-val">{birthPlaceDate}</span>
                    </div>
                    <div className="ead-exec-field">
                      <span className="ead-exec-label">Gender</span>
                      <span className="ead-exec-val">{gender}</span>
                    </div>
                    <div className="ead-exec-field">
                      <span className="ead-exec-label">Nationality &amp; Religion</span>
                      <span className="ead-exec-val">{nationality} &bull; {religion}</span>
                    </div>
                    <div className="ead-exec-field">
                      <span className="ead-exec-label">Previous School</span>
                      <span className="ead-exec-val">{previousSchool}</span>
                    </div>
                  </div>
                </div>

                {/* 6. PARENT PARTICULARS */}
                <div className="ead-exec-section" style={{ marginTop: '8px' }}>
                  <div className="ead-exec-section-header">
                    <Building2 size={13} />
                    <span>2. Parent / Guardian Particulars</span>
                  </div>
                  <div className="ead-exec-grid">
                    <div className="ead-exec-field">
                      <span className="ead-exec-label">Parent / Guardian</span>
                      <span className="ead-exec-val">{parentName}</span>
                    </div>
                    <div className="ead-exec-field">
                      <span className="ead-exec-label">Father / Mother</span>
                      <span className="ead-exec-val">{fatherName} / {motherName}</span>
                    </div>
                    <div className="ead-exec-field">
                      <span className="ead-exec-label">Contact / WhatsApp</span>
                      <span className="ead-exec-val">{parentPhone}</span>
                    </div>
                    <div className="ead-exec-field">
                      <span className="ead-exec-label">Email Address</span>
                      <span className="ead-exec-val">{parentEmail}</span>
                    </div>
                    <div className="ead-exec-field full-width">
                      <span className="ead-exec-label">Home Address</span>
                      <span className="ead-exec-val">{parentAddress}</span>
                    </div>
                    {parentOccupation && parentOccupation !== '-' && (
                      <div className="ead-exec-field full-width">
                        <span className="ead-exec-label">Occupation</span>
                        <span className="ead-exec-val">{parentOccupation}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* 7. ENTRANCE ASSESSMENT (CLEAN CALLOUT ROW) */}
                {app.test_score !== null && app.test_score !== undefined && (
                  <div className="ead-exec-assessment">
                    <div>
                      <strong>Entrance Assessment Status:</strong> Passed evaluation with an overall score of{' '}
                      <span style={{ fontWeight: 800 }}>{app.test_score} / 100</span>
                      {app.test_evaluation_notes && (
                        <span style={{ fontStyle: 'italic', marginLeft: '6px' }}>
                          &mdash; &ldquo;{app.test_evaluation_notes}&rdquo;
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Notice Pointer */}
                <div className="ead-exec-notice">
                  * Complete Schedule of Admission Fees, Remittance Account, and Signatory Governance are presented on Page 2.
                </div>
              </div>

              {/* CLEAN FOOTER PAGE 1 */}
              <div className="ead-exec-footer">
                <div>
                  <strong>{branding.schoolName}</strong> &bull; Campus 1: Jl. Pos Pengumben Lama No. 13 &bull; Campus 2: Jl. Pos Pengumben No. 35, Jakarta<br />
                  Helpdesk: (021) 5890 1100 &bull; admissions@permataharapansecondary.sch.id &bull; https://permataharapansecondary.sch.id
                </div>
                <div>
                  <span className="ead-exec-page-tag">- Page 1 of 2 -</span>
                </div>
              </div>
            </div>

            {/* ===================================================================
                PAGE 2 (PAGE 2 OF 2): SCHEDULE OF FEES & THREE-COLUMN SIGNATORIES
            =================================================================== */}
            <div className="ead-sheet ead-sheet-loa-page ead-loa-page-2">
              <div>
                {/* 1. RUNNING HEADER PAGE 2 */}
                <div className="ead-exec-header" style={{ paddingBottom: '8px', marginBottom: '8px' }}>
                  <div className="ead-exec-brand">
                    <div className="ead-exec-logo-wrap">
                      <img
                        src={branding.logo}
                        alt={branding.schoolName}
                        className={`ead-kop-logo ead-kop-logo-sm ${branding.logoClass}`}
                        onError={(e) => {
                          if (branding.publicFallback && e.currentTarget.src !== branding.publicFallback) {
                            e.currentTarget.src = branding.publicFallback;
                          }
                        }}
                      />
                    </div>
                    <div className="ead-exec-titles">
                      <span className="ead-exec-foundation" style={{ fontSize: '0.62rem' }}>{branding.foundation}</span>
                      <span className="ead-exec-school" style={{ fontSize: '0.98rem' }}>{branding.schoolName}</span>
                      <span className="ead-exec-directorate" style={{ fontSize: '0.66rem' }}>{branding.directorate}</span>
                    </div>
                  </div>
                  <div className="ead-exec-page-tag">
                    - Page 2 of 2 -
                  </div>
                </div>

                {/* 2. ANNEX SUMMARY STRIP */}
                <div className="ead-exec-annex-strip">
                  <div>
                    <strong>ANNEX: COMPREHENSIVE SCHEDULE OF ADMISSION FEES</strong>
                  </div>
                  <div>
                    Ref: <strong>{loaNumber}</strong> &bull; Candidate: <strong>{childName}</strong> ({regNo})
                  </div>
                </div>

                {/* 3. FEE TABLE */}
                <table className="ead-exec-table">
                  <thead>
                    <tr>
                      <th style={{ width: '32px', textAlign: 'center' }}>No</th>
                      <th>Official Admission Fee Component</th>
                      <th className="text-right" style={{ width: '160px' }}>Amount (IDR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: 'center' }}>1</td>
                      <td>
                        <strong>Registration &amp; Application Form Fee</strong>
                        <div className="ead-exec-termin-subtext">Administrative processing of official candidate registration file</div>
                      </td>
                      <td className="text-right">{formatCurrencyIDR(regFee)}</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: 'center' }}>2</td>
                      <td>
                        <strong>Entrance Assessment &amp; Observation Fee</strong>
                        <div className="ead-exec-termin-subtext">Academic readiness evaluation, developmental observation &amp; interview</div>
                      </td>
                      <td className="text-right">{formatCurrencyIDR(testFee)}</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: 'center' }}>3</td>
                      <td>
                        <strong>Admission &amp; Enrollment Fee (Matriculation)</strong>
                        <div className="ead-exec-termin-subtext">Official enrollment confirmation, student registration &amp; seat allocation</div>
                      </td>
                      <td className="text-right">{formatCurrencyIDR(admissionFee)}</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: 'center' }}>4</td>
                      <td>
                        <strong>Development Fee (Uang Pangkal) - Total</strong>
                        <div className="ead-exec-termin-subtext">
                          Installment Plan: Term 1: {formatCurrencyIDR(df1)} &bull; Term 2: {formatCurrencyIDR(df2)} &bull; Term 3: {formatCurrencyIDR(df3)}
                          {df4 > 0 && ` • Term 4: ${formatCurrencyIDR(df4)}`}
                        </div>
                      </td>
                      <td className="text-right">{formatCurrencyIDR(devFee)}</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: 'center' }}>5</td>
                      <td>
                        <strong>Books, Uniform &amp; Learning Activity Package</strong>
                        <div className="ead-exec-termin-subtext">Complete curriculum textbooks, school uniform set &amp; student activities</div>
                      </td>
                      <td className="text-right">{formatCurrencyIDR(booksFee)}</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: 'center' }}>6</td>
                      <td>
                        <strong>First Month Tuition Fee (SPP)</strong>
                        <div className="ead-exec-termin-subtext">Instructional tuition coverage for the first academic month of AY {academicYear}</div>
                      </td>
                      <td className="text-right">{formatCurrencyIDR(tuitionFee)}</td>
                    </tr>
                    {discountAmount > 0 && (
                      <tr style={{ color: '#059669' }}>
                        <td style={{ textAlign: 'center' }}>-</td>
                        <td>
                          <strong>Approved Institutional Incentive / Discount ({discountLabel})</strong>
                          <div className="ead-exec-termin-subtext" style={{ color: '#059669' }}>Official promotional incentive approved for this registration intake</div>
                        </td>
                        <td className="text-right" style={{ fontWeight: 700 }}>-{formatCurrencyIDR(discountAmount)}</td>
                      </tr>
                    )}
                    <tr className="ead-exec-table-total">
                      <td colSpan="2">TOTAL ADMISSION INVESTMENT (TOTAL PAYABLE)</td>
                      <td className="text-right">{formatCurrencyIDR(totalPayable)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* 4. REMITTANCE ACCOUNT STRIP */}
                <div className="ead-exec-remittance">
                  <div className="ead-exec-remittance-grid">
                    <div className="ead-exec-remittance-item">
                      <small>Beneficiary Bank</small>
                      <strong>{bankName}</strong>
                    </div>
                    <div className="ead-exec-remittance-item">
                      <small>Official Account Number</small>
                      <strong>{bankAccount}</strong>
                    </div>
                    <div className="ead-exec-remittance-item">
                      <small>Account Holder Name</small>
                      <strong>{bankHolder}</strong>
                    </div>
                  </div>
                  <div className="ead-exec-remittance-note">
                    * Please complete payment and upload the official transfer receipt via the Parent Admissions Portal according to the stipulated timeline to secure seat reservation.
                  </div>
                </div>

                {/* 5. THREE-COLUMN SIGNATORIES */}
                <div className="ead-exec-signatures">
                  {/* Column 1: Parent / Guardian */}
                  <div className="ead-exec-sig-col">
                    <div className="ead-exec-sig-title">Approved by Parent / Guardian</div>
                    <div className="ead-exec-sig-space">
                      {/* Space for wet-ink physical signature upon printout */}
                    </div>
                    <div className="ead-exec-sig-line" />
                    <p className="ead-exec-sig-name">( {parentName} )</p>
                    <p className="ead-exec-sig-role">Parent / Guardian</p>
                    <p className="ead-exec-sig-sub">*Physical wet-ink signature upon printout</p>
                  </div>

                  {/* Column 2: Admissions Committee */}
                  <div className="ead-exec-sig-col">
                    <div className="ead-exec-sig-title">Verified by Admissions Committee</div>
                    <div className="ead-exec-sig-space">
                      <div className="ead-official-badge">
                        <ShieldCheck size={12} />
                        <span>Admissions Verified</span>
                      </div>
                    </div>
                    <div className="ead-exec-sig-line" />
                    <p className="ead-exec-sig-name">{officerName}</p>
                    <p className="ead-exec-sig-role">{officerRole} &bull; {branding.schoolName}</p>
                    <p className="ead-exec-sig-sub">Admissions Committee</p>
                  </div>

                  {/* Column 3: School Director */}
                  <div className="ead-exec-sig-col">
                    <div className="ead-exec-sig-title">Endorsed by School Director</div>
                    <div className="ead-exec-sig-space">
                      <div className="ead-sig-stamp">
                        {branding.sealText.split('\n').map((line, idx) => (
                          <React.Fragment key={idx}>{line}<br /></React.Fragment>
                        ))}
                      </div>
                    </div>
                    <div className="ead-exec-sig-line" />
                    <p className="ead-exec-sig-name">{directorName}</p>
                    <p className="ead-exec-sig-role">{directorRole} &bull; {branding.schoolName}</p>
                    <p className="ead-exec-sig-sub">Official Directorate Endorsement</p>
                  </div>
                </div>
              </div>

              {/* CLEAN FOOTER PAGE 2 */}
              <div className="ead-exec-footer">
                <div>
                  <strong>{branding.schoolName}</strong> &bull; Official Admissions &amp; Financial Governance Document<br />
                  This document is issued and endorsed by the Admissions &amp; Academic Enrollment Directorate.
                </div>
                <div>
                  <span className="ead-exec-page-tag">- Page 2 of 2 -</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 2. OFFICIAL CERTIFICATE OF ADMISSION & STUDENT ENROLLMENT (Bukti Siswa Resmi)
 * Formal English Certificate confirming the child is a registered student with NIS
 */
export function OfficialEnrollmentCertificateModal({ app, onClose }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  if (!app) return null;

  const childName = app.child_full_name || app.student_name || app.name || 'Student Candidate';
  const nis = app.student_id_number || `PHS-2026-${String(app.id || 1).padStart(4, '0')}`;
  const enrolledDate = formatFormalDate(app.enrolled_at || app.payment_verified_at || app.updated_at);
  const academicYear = app.academic_year || '2026/2027';
  const gradeTarget = app.grade_target || app.level_name || 'Primary School';
  const levelName = app.level || app.level_name || 'Sekolah Permata Harapan';
  const campus = app.campus_location || 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13';

  const branding = getLevelBranding(app.level_code || app.level || app.level_name, gradeTarget);
  const officerName = app.admissions_officer_name || 'Ivana';
  const officerRole = 'Admissions Officer';
  const directorName = app.school_director_name || app.school_principal_name || branding.directorName;
  const directorRole = 'School Director';

  const formData = app.form_data || {};
  const birthPlace = app.birth_place || formData.birth_place || '-';
  const birthDate = formatFormalDate(app.birth_date || formData.birth_date);
  const gender = app.gender || formData.gender || '-';
  const nationality = app.nationality || formData.nationality || 'Indonesian (WNI)';

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Official_Student_Enrollment_Certificate_${childName.replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownloadPdf = async () => {
    const sheetEl = document.querySelector('.ead-sheet-cert');
    if (!sheetEl) return;
    try {
      setDownloadingPdf(true);
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Official_Student_Enrollment_Certificate_${childName.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(sheetEl).save();
    } catch (err) {
      console.error('PDF error, fallback to print:', err);
      handlePrint();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="ead-modal-overlay" onClick={onClose}>
      <div className="ead-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ead-modal-toolbar">
          <div className="ead-toolbar-title">
            <CheckCircle2 size={18} />
            <span>Official Student Enrollment Certificate - {childName}</span>
          </div>
          <div className="ead-toolbar-actions">
            <button
              type="button"
              className="ead-btn-download"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <Download size={15} />
              <span>{downloadingPdf ? 'Menyimpan PDF...' : 'Simpan / Unduh PDF (.pdf)'}</span>
            </button>
            <button type="button" className="ead-btn-print" onClick={handlePrint}>
              <Printer size={15} />
              <span>Cetak Dokumen</span>
            </button>
            <button type="button" className="ead-btn-close" onClick={onClose} aria-label="Tutup">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ead-paper-scroll">
          <div className="ead-sheet ead-sheet-cert" style={{ border: '2px solid #0f172a', padding: '44px 50px' }}>
            {/* Header */}
            <div className="ead-header" style={{ borderBottom: '3px double #0f172a' }}>
              <div className="ead-header-inner">
                <div className="ead-brand-col">
                  <div className={`ead-logo-box ${branding.boxClass}`}>
                    <img
                      src={branding.logo}
                      alt={branding.schoolName}
                      className={`ead-school-logo ${branding.logoClass}`}
                      onError={(e) => {
                        if (branding.publicFallback && e.currentTarget.src !== branding.publicFallback) {
                          e.currentTarget.src = branding.publicFallback;
                        }
                      }}
                    />
                  </div>
                  <div className="ead-brand-text">
                    <h1 style={{ letterSpacing: '0.05em' }}>{branding.schoolName}</h1>
                    <p>{branding.directorate}</p>
                  </div>
                </div>
                <div className="ead-contact-col">
                  <strong>Sekolah Permata Harapan</strong><br />
                  Jl. Pos Pengumben Lama No. 13, Srengseng, Kembangan, Jakarta Barat 11630<br />
                  Phone: (021) 5890 1100 • Email: admissions@permataharapansecondary.sch.id<br />
                  Website: https://permataharapansecondary.sch.id
                </div>
              </div>
            </div>

            {/* Document Title */}
            <div className="ead-doc-title-block" style={{ margin: '24px 0 28px' }}>
              <div style={{ textTransform: 'uppercase', letterSpacing: '0.15em', fontSize: '0.8rem', color: '#64748b', fontWeight: 700 }}>
                Official Academic Credential
              </div>
              <h2 className="ead-doc-title" style={{ fontSize: '1.6rem', marginTop: '6px' }}>
                Certificate of Official Enrollment
              </h2>
              <div className="ead-doc-subtitle" style={{ fontSize: '0.9rem', color: '#334155' }}>
                This is to certify that the individual named below is a duly enrolled student of Sekolah Permata Harapan
              </div>
            </div>

            {/* Certificate Body */}
            <p className="ead-paragraph" style={{ textAlign: 'center', fontSize: '0.95rem', margin: '0 0 20px' }}>
              The Office of Admissions &amp; Registrar hereby certifies and confirms that:
            </p>

            <div className="ead-highlight-box" style={{ background: '#fdfdfe', border: '2px solid #0f172a', padding: '24px 28px' }}>
              <div className="ead-grid-2">
                <div className="ead-grid-item" style={{ gridColumn: 'span 2', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                  <span className="ead-grid-label">Full Name of Student</span>
                  <span className="ead-grid-value" style={{ fontSize: '1.25rem', color: '#0f172a' }}>{childName}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Student Identification Number (NIS)</span>
                  <span className="ead-grid-value" style={{ color: '#2563eb', fontSize: '1.1rem' }}>{nis}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Academic Program &amp; Level</span>
                  <span className="ead-grid-value">{gradeTarget} ({levelName})</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Place &amp; Date of Birth</span>
                  <span className="ead-grid-value">{birthPlace}, {birthDate}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Gender &amp; Nationality</span>
                  <span className="ead-grid-value">{gender} / {nationality}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Enrolled Campus Location</span>
                  <span className="ead-grid-value">{campus}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Academic Year</span>
                  <span className="ead-grid-value">{academicYear}</span>
                </div>
              </div>
            </div>

            <p className="ead-paragraph" style={{ marginTop: '20px' }}>
              The student named above has completed all required entrance evaluations, submitted verified documentation, and fulfilled all admission prerequisites and institutional matriculation protocols. In witness whereof, the student is officially admitted and listed in the active registry of Sekolah Permata Harapan with all associated academic rights and institutional privileges.
            </p>

            <div className="ead-meta-bar" style={{ marginTop: '24px', border: 'none', background: '#f8fafc', padding: '8px 16px', borderRadius: '4px' }}>
              <div><strong>Effective Date of Enrollment:</strong> {enrolledDate}</div>
              <div><strong>Registry Status:</strong> <span style={{ color: '#059669', fontWeight: 700 }}>Active &amp; Verified</span></div>
            </div>

            {/* Signatures */}
            <div className="ead-signature-section" style={{ marginTop: '32px' }}>
              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-official-badge">
                    <ShieldCheck size={14} />
                    <span>Academic Registrar</span>
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{officerName}</p>
                <p className="ead-sig-role">{officerRole} • Office of the Registrar</p>
              </div>

              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-sig-stamp">
                    SEKOLAH<br />PERMATA<br />HARAPAN
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{directorName}</p>
                <p className="ead-sig-role">{directorRole} • Sekolah Permata Harapan</p>
              </div>
            </div>

            {/* Footer */}
            <div className="ead-footer-note">
              This certificate confirms official student status and is recognized for official transfer, consular, and academic identification purposes.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 3. OFFICIAL PAYMENT RECEIPT & STATEMENT OF ACCOUNT (Kwitansi Pembayaran Resmi)
 * Formal English Receipt for Tuition & Development Fees (No Allocation / Period column)
 */
export function OfficialPaymentReceiptModal({ app, onClose }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  if (!app) return null;

  const childName = app.child_full_name || app.student_name || app.name || 'Student Candidate';
  const parentName = app.parent_info?.full_name || app.applicant_name || 'Parent / Guardian';
  const nis = app.student_id_number || `PHS-2026-${String(app.id || 1).padStart(4, '0')}`;
  const receiptNo = app.receipt_number || app.invoice?.receipt_no || `RCP-PH/2026/${app.id || '0001'}`;
  const verifiedDate = formatFormalDate(app.payment_verified_at || app.payment_submitted_at || app.updated_at);
  const paymentMethod = app.payment_method || 'Bank Transfer (BCA - Bank Central Asia)';
  const academicYear = app.academic_year || '2026/2027';
  const gradeTarget = app.grade_target || app.level_name || 'Primary School';
  const branding = getLevelBranding(app.level_code || app.level || app.level_name, gradeTarget);

  const officerName = app.admissions_officer_name || 'Ivana';
  const financeName = app.finance_pic_name || 'Zahra, S.E.';
  const financeRole = app.finance_pic_title || 'Finance & Treasury PIC';

  const inv = app.invoice || {};
  const regFee = Number(inv.registration_fee || 0);
  const admissionFee = Number(inv.admission_fee || 0);
  const devFee = Number(inv.development_fee || 0);
  const booksFee = Number(inv.books_activity_fee || 0);
  const tuitionFee = Number(inv.tuition_fee || 0);
  const discountAmount = Number(inv.discount_amount || app.discount_amount || 0);
  const totalPayable = Number(inv.total_payable || 0);
  const amountPaid = Number(app.payment_amount_paid || inv.amount_paid || totalPayable);

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Official_Payment_Receipt_${childName.replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownloadPdf = async () => {
    const sheetEl = document.querySelector('.ead-sheet-receipt');
    if (!sheetEl) return;
    try {
      setDownloadingPdf(true);
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Official_Payment_Receipt_${childName.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(sheetEl).save();
    } catch (err) {
      console.error('PDF error, fallback to print:', err);
      handlePrint();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="ead-modal-overlay" onClick={onClose}>
      <div className="ead-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ead-modal-toolbar">
          <div className="ead-toolbar-title">
            <CreditCard size={18} />
            <span>Official Payment Receipt &amp; Invoice - {childName}</span>
          </div>
          <div className="ead-toolbar-actions">
            <button
              type="button"
              className="ead-btn-download"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <Download size={15} />
              <span>{downloadingPdf ? 'Menyimpan PDF...' : 'Simpan / Unduh PDF (.pdf)'}</span>
            </button>
            <button type="button" className="ead-btn-print" onClick={handlePrint}>
              <Printer size={15} />
              <span>Cetak Dokumen</span>
            </button>
            <button type="button" className="ead-btn-close" onClick={onClose} aria-label="Tutup">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ead-paper-scroll">
          <div className="ead-sheet ead-sheet-receipt">
            {/* Header */}
            <div className="ead-header">
              <div className="ead-header-inner">
                <div className="ead-brand-col">
                  <div className={`ead-logo-box ${branding.boxClass}`}>
                    <img
                      src={branding.logo}
                      alt={branding.schoolName}
                      className={`ead-school-logo ${branding.logoClass}`}
                      onError={(e) => {
                        if (branding.publicFallback && e.currentTarget.src !== branding.publicFallback) {
                          e.currentTarget.src = branding.publicFallback;
                        }
                      }}
                    />
                  </div>
                  <div className="ead-brand-text">
                    <h1>{branding.schoolName}</h1>
                    <p>Bursar &amp; Student Financial Services Directorate</p>
                  </div>
                </div>
                <div className="ead-contact-col">
                  <strong>Yayasan Pendidikan Permata Harapan</strong><br />
                  Jl. Pos Pengumben Lama No. 13, Srengseng, Kembangan, Jakarta Barat 11630<br />
                  Tax ID / NPWP: 01.345.678.9-082.000 • Phone: (021) 5890 1100<br />
                  Email: admissions@permataharapansecondary.sch.id
                </div>
              </div>
            </div>

            {/* Reference Bar */}
            <div className="ead-meta-bar">
              <div><strong>Receipt Number:</strong> <span style={{ color: '#2563eb' }}>{receiptNo}</span></div>
              <div><strong>Payment Verified Date:</strong> {verifiedDate}</div>
            </div>

            {/* Title */}
            <div className="ead-doc-title-block">
              <h2 className="ead-doc-title">Official Payment Receipt</h2>
              <div className="ead-doc-subtitle">Statement of Academic Account &amp; Settlement Confirmation</div>
            </div>

            {/* Account Details Box */}
            <div className="ead-highlight-box" style={{ background: '#f8fafc' }}>
              <div className="ead-grid-2">
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Received From (Payor)</span>
                  <span className="ead-grid-value">{parentName}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Student Name &amp; NIS</span>
                  <span className="ead-grid-value">{childName} (NIS: {nis})</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Program &amp; Academic Year</span>
                  <span className="ead-grid-value">{gradeTarget} - TA {academicYear}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Settlement Channel</span>
                  <span className="ead-grid-value">{paymentMethod}</span>
                </div>
              </div>
            </div>

            {/* Itemized Table without Allocation / Period */}
            <table className="ead-table">
              <thead>
                <tr>
                  <th style={{ width: '45px' }}>No</th>
                  <th>Fee Description</th>
                  <th className="text-right" style={{ width: '180px' }}>Amount (IDR)</th>
                </tr>
              </thead>
              <tbody>
                {admissionFee > 0 && (
                  <tr>
                    <td>1</td>
                    <td>Admission &amp; Matriculation Fee</td>
                    <td className="text-right">{formatCurrencyIDR(admissionFee + regFee)}</td>
                  </tr>
                )}
                {devFee > 0 && (
                  <tr>
                    <td>2</td>
                    <td>Development Fee (Uang Pangkal)</td>
                    <td className="text-right">{formatCurrencyIDR(devFee)}</td>
                  </tr>
                )}
                {booksFee > 0 && (
                  <tr>
                    <td>3</td>
                    <td>Books, Learning Materials &amp; Activity Fee</td>
                    <td className="text-right">{formatCurrencyIDR(booksFee)}</td>
                  </tr>
                )}
                {tuitionFee > 0 && (
                  <tr>
                    <td>4</td>
                    <td>Monthly Tuition Fee (SPP)</td>
                    <td className="text-right">{formatCurrencyIDR(tuitionFee)}</td>
                  </tr>
                )}
                {discountAmount > 0 && (
                  <tr className="ead-discount-row">
                    <td>-</td>
                    <td>Approved Institutional Discount / Voucher ({inv.discount_label || app.promo_code_used || 'Approved Incentive'})</td>
                    <td className="text-right">-{formatCurrencyIDR(discountAmount)}</td>
                  </tr>
                )}
                <tr className="ead-total-row">
                  <td colSpan="2">TOTAL AMOUNT PAID &amp; VERIFIED</td>
                  <td className="text-right" style={{ color: '#059669', fontSize: '1.05rem' }}>
                    {formatCurrencyIDR(amountPaid > 0 ? amountPaid : totalPayable)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Payment Status Stamp */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '20px 0', background: '#ecfdf5', padding: '12px 18px', borderRadius: '6px', border: '1px solid #a7f3d0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CheckCircle2 size={22} color="#059669" />
                <div>
                  <strong style={{ color: '#065f46', fontSize: '0.9rem' }}>STATUS: PAYMENT VERIFIED &amp; CLEARED</strong><br />
                  <span style={{ fontSize: '0.78rem', color: '#047857' }}>
                    Funds credited to Yayasan Pendidikan Permata Harapan (BCA Account 548-098-7788)
                  </span>
                </div>
              </div>
              <div style={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 800, color: '#059669', border: '2px solid #059669', padding: '4px 10px', borderRadius: '4px', letterSpacing: '0.08em' }}>
                OFFICIAL PAID
              </div>
            </div>

            <p className="ead-paragraph" style={{ fontSize: '0.78rem', color: '#64748b', fontStyle: 'italic', margin: '14px 0 0' }}>
              * Important Institutional Notice: In accordance with standard school admissions regulations, all payments made to Yayasan Pendidikan Permata Harapan are strictly non-refundable and non-transferable under any circumstances once processed.
            </p>

            {/* Signatures */}
            <div className="ead-signature-section">
              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-official-badge">
                    <ShieldCheck size={14} />
                    <span>Admissions Verification</span>
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{officerName}</p>
                <p className="ead-sig-role">Admissions &amp; Verification Officer</p>
              </div>

              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-sig-stamp">
                    YAYASAN<br />PERMATA<br />HARAPAN
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{financeName}</p>
                <p className="ead-sig-role">{financeRole} • Yayasan Pendidikan Permata Harapan</p>
              </div>
            </div>

            {/* Footer */}
            <div className="ead-footer-note">
              This receipt is computer-generated and officially acknowledged by the Bursar Office of Sekolah Permata Harapan.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 4. KARTU TANDA PESERTA TES & OBSERVASI PPDB (OFFICIAL EXAMINATION PASS)
 * Official Admission Test & Observation Card for offline entrance assessment
 */
export function ExaminationPassModal({ app, onClose }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  if (!app) return null;

  const childName = app.child_full_name || app.student_name || app.name || 'Calon Siswa';
  const regNo = app.registration_no || `REG-PHS-${app.id || '0001'}`;
  const periodName = app.period_name || 'Gelombang 1 (Early Bird)';
  const academicYear = app.academic_year || '2026/2027';
  const gradeTarget = app.grade_target || app.level_name || 'Secondary 1 (Grade 7)';
  const levelName = app.level_name || app.level || 'Secondary School / SMP';
  const branding = getLevelBranding(app.level_code || app.level || app.level_name, gradeTarget);

  const testSched = app.test_schedule || {};
  const slotName = testSched.slot_name || app.test_slot_name || 'Sesi Tes & Observasi Masuk';
  const testDate = formatFormalDate(testSched.test_date || app.test_date);
  const testTime = testSched.start_time && testSched.end_time
    ? `${testSched.start_time} - ${testSched.end_time} WIB`
    : app.test_time || '09:00 - 11:30 WIB';
  const campus = testSched.campus_location || app.campus_location || 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13';
  const room = testSched.room_name || app.room_name || 'Ruang Observasi & Akademik';
  const assessmentTitle = testSched.assessment_title || app.assessment_title || app.assessment_info?.title || 'Cambridge Entrance Assessment & Academic Interview';
  const assessmentSubjects = testSched.assessment_subjects || app.assessment_subjects || app.assessment_info?.subjects || 'English Proficiency, Mathematics, Integrated Science & Family Interview';
  const assessmentPreparation = testSched.assessment_preparation || app.assessment_preparation || app.assessment_info?.preparation || '';
  const assessmentRulesRaw = testSched.assessment_rules || app.assessment_rules || app.assessment_info?.rules || '';
  const parentInstructions = testSched.parent_instructions || app.parent_instructions || app.assessment_info?.parent_instructions || '';

  const preparationList = assessmentPreparation
    ? assessmentPreparation.split(/\r?\n/).map(line => line.replace(/^[\d+\.\-\*\•\s]+/, '').trim()).filter(Boolean)
    : [];

  const rulesList = assessmentRulesRaw
    ? assessmentRulesRaw.split(/\r?\n/).map(line => line.replace(/^[\d+\.\-\*\•\s]+/, '').trim()).filter(Boolean)
    : [
        'Peserta wajib hadir di lokasi kampus Sekolah Permata Harapan minimal 15 (lima belas) menit sebelum waktu ujian dimulai.',
        'Membawa Kartu Tanda Peserta ini (telah dicetak) dan alat tulis lengkap (pensil 2B, penghapus, penggaris, dan pulpen).',
        'Mengenakan pakaian seragam sekolah asal atau pakaian rapi, sopan, berkerah, dan bersepatu.',
        'Kartu Tanda Peserta ini wajib ditunjukkan kepada Petugas Registrasi & Pengawas Ruang pada saat verifikasi kehadiran.',
        'Orang tua / wali pendamping wajib menunggu di area drop-off / ruang tunggu yang telah disediakan oleh panitia.'
      ];

  const formData = app.form_data || {};
  const birthPlace = app.birth_place || formData.birth_place || 'Jakarta';
  const birthDate = formatFormalDate(app.birth_date || formData.birth_date);
  const gender = (app.gender || formData.gender || '').toLowerCase() === 'female' ? 'Perempuan' : 'Laki-laki';
  const previousSchool = app.previous_school || formData.previous_school || '-';
  const parentName = app.father_name || app.mother_name || app.parent_info?.full_name || app.applicant_name || 'Orang Tua / Wali';
  const parentPhone = app.parent_phone || formData.parent_phone || '-';
  const photoUrl = app.student_photo_url || app.pass_photo_url || app.photo_url || formData.student_photo_url;
  const officerName = app.admissions_officer_name || 'Panitia Penerimaan Siswa Baru';

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Kartu_Peserta_Tes_${childName.replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownloadPdf = async () => {
    const sheetEl = document.querySelector('.ead-sheet-exam');
    if (!sheetEl) return;
    try {
      setDownloadingPdf(true);
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Kartu_Peserta_Tes_${childName.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(sheetEl).save();
    } catch (err) {
      console.error('PDF error, fallback to print:', err);
      handlePrint();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="ead-modal-overlay" onClick={onClose}>
      <div className="ead-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ead-modal-toolbar">
          <div className="ead-toolbar-title">
            <Award size={18} />
            <span>Kartu Tanda Peserta Tes &amp; Observasi PPDB - {childName}</span>
          </div>
          <div className="ead-toolbar-actions">
            <button
              type="button"
              className="ead-btn-download"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <Download size={15} />
              <span>{downloadingPdf ? 'Menyimpan PDF...' : 'Simpan / Unduh PDF (.pdf)'}</span>
            </button>
            <button type="button" className="ead-btn-print" onClick={handlePrint}>
              <Printer size={15} />
              <span>Cetak Dokumen</span>
            </button>
            <button type="button" className="ead-btn-close" onClick={onClose} aria-label="Tutup">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ead-paper-scroll">
          <div className="ead-sheet ead-sheet-exam">
            {/* Header Kop Surat */}
            <div className="ead-header" style={{ paddingBottom: 12 }}>
              <div className="ead-header-inner">
                <div className="ead-brand-col">
                  <div className={`ead-logo-box ${branding.boxClass}`}>
                    <img
                      src={branding.logo}
                      alt={branding.schoolName}
                      className={`ead-school-logo ${branding.logoClass}`}
                      onError={(e) => {
                        if (branding.publicFallback && e.currentTarget.src !== branding.publicFallback) {
                          e.currentTarget.src = branding.publicFallback;
                        }
                      }}
                    />
                  </div>
                  <div className="ead-brand-text">
                    <h1>{branding.schoolName}</h1>
                    <p>Panitia Penerimaan Peserta Didik Baru (PPDB)</p>
                  </div>
                </div>
                <div className="ead-contact-col">
                  <strong>Sekolah Permata Harapan</strong><br />
                  Jl. Pos Pengumben Lama No. 13, Srengseng, Kembangan, Jakarta Barat 11630<br />
                  Phone: (021) 5890 1100 • Email: admissions@permataharapansecondary.sch.id<br />
                  Website: https://permataharapansecondary.sch.id
                </div>
              </div>
            </div>

            {/* Document Title Banner */}
            <div className="ead-exam-title-banner">
              <h2>KARTU TANDA PESERTA SELEKSI MASUK &amp; OBSERVASI</h2>
              <div className="ead-exam-subtitle">
                Tahun Pelajaran {academicYear} • {levelName}
              </div>
            </div>

            {/* Two-Column Candidate Profile & Photo Section */}
            <div className="ead-exam-profile-grid">
              <div className="ead-exam-photo-col">
                <div className="ead-exam-photo-card">
                  {photoUrl ? (
                    <img src={photoUrl} alt={childName} className="ead-exam-photo-img" />
                  ) : (
                    <div className="ead-exam-photo-placeholder">
                      <User size={36} color="#94a3b8" />
                      <span>Pas Foto 4x3 Calon Siswa</span>
                    </div>
                  )}
                </div>
                <div className="ead-exam-reg-badge">
                  <small>Nomor Registrasi / Peserta</small>
                  <strong>{regNo}</strong>
                </div>
                <div className="ead-exam-period-badge">
                  {periodName}
                </div>
              </div>

              <div className="ead-exam-info-col">
                <table className="ead-exam-table">
                  <tbody>
                    <tr>
                      <td className="ead-col-label">Nama Lengkap Siswa</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val"><strong>{childName}</strong></td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Jenjang &amp; Kelas Dituju</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{levelName} — {gradeTarget}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Tempat, Tanggal Lahir</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{birthPlace}, {birthDate}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Jenis Kelamin</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{gender}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Asal Sekolah</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{previousSchool}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Nama Orang Tua / Wali</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{parentName}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Kontak / No. WhatsApp</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{parentPhone}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Test Schedule & Location Box */}
            <div className="ead-exam-sched-card">
              <div className="ead-exam-sched-head">
                <Calendar size={16} />
                <span>Jadwal &amp; Lokasi Pelaksanaan Tes Offline</span>
              </div>
              <div className="ead-exam-sched-grid">
                <div>
                  <small>Hari &amp; Tanggal</small>
                  <strong>{testDate}</strong>
                </div>
                <div>
                  <small>Waktu Pelaksanaan</small>
                  <strong>{testTime}</strong>
                </div>
                <div>
                  <small>Sesi Tes</small>
                  <strong>{slotName}</strong>
                </div>
                <div>
                  <small>Lokasi Kampus &amp; Ruangan</small>
                  <strong>{campus} ({room})</strong>
                </div>
              </div>
            </div>

            {/* Assessment Focus */}
            <div className="ead-exam-subjects-card">
              <strong>Fokus Materi Asesmen:</strong> {assessmentTitle}
              <div style={{ marginTop: 3, color: '#334155', fontSize: '0.8rem' }}>
                {assessmentSubjects}
              </div>
            </div>

            {/* Required Preparation Items */}
            {preparationList.length > 0 && (
              <div className="ead-exam-prep-card" style={{ marginTop: 8, padding: '8px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                  Perlengkapan yang Wajib Dibawa Siswa:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.75rem', color: '#334155', lineHeight: 1.45 }}>
                  {preparationList.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: 2 }}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Exam Rules & Instructions */}
            <div className="ead-exam-rules-card" style={{ marginTop: 8 }}>
              <h4>Tata Tertib &amp; Ketentuan Peserta Tes:</h4>
              <ol className="ead-exam-rules-list">
                {rulesList.map((rule, idx) => (
                  <li key={idx}>{rule}</li>
                ))}
              </ol>
            </div>

            {/* Special Instructions for Parents / Waiting Area */}
            {parentInstructions && (
              <div style={{ marginTop: 8, padding: '6px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: '0.73rem', color: '#1e40af', lineHeight: 1.4 }}>
                <strong>Catatan Khusus Orang Tua / Pendamping:</strong> {parentInstructions}
              </div>
            )}

            {/* Signatures & QR Code Verification */}
            <div className="ead-exam-sig-row">
              <div className="ead-exam-sig-col">
                <p className="ead-exam-sig-title">Tanda Tangan Calon Siswa,</p>
                <div className="ead-exam-sig-space" />
                <div className="ead-exam-sig-line" />
                <p className="ead-exam-sig-name">{childName}</p>
                <small className="ead-exam-sig-note">Tanda tangan pada saat hadir di ruang tes</small>
              </div>

              <div className="ead-exam-qr-col">
                <div className="ead-qr-frame">
                  <svg width="84" height="84" viewBox="0 0 100 100">
                    <rect width="100" height="100" fill="#ffffff" />
                    <rect x="10" y="10" width="24" height="24" fill="#0f172a" />
                    <rect x="14" y="14" width="16" height="16" fill="#ffffff" />
                    <rect x="18" y="18" width="8" height="8" fill="#0f172a" />
                    <rect x="66" y="10" width="24" height="24" fill="#0f172a" />
                    <rect x="70" y="14" width="16" height="16" fill="#ffffff" />
                    <rect x="74" y="18" width="8" height="8" fill="#0f172a" />
                    <rect x="10" y="66" width="24" height="24" fill="#0f172a" />
                    <rect x="14" y="70" width="16" height="16" fill="#ffffff" />
                    <rect x="18" y="74" width="8" height="8" fill="#0f172a" />
                    <rect x="42" y="14" width="6" height="6" fill="#0f172a" />
                    <rect x="52" y="24" width="6" height="6" fill="#0f172a" />
                    <rect x="42" y="34" width="6" height="6" fill="#0f172a" />
                    <rect x="52" y="44" width="6" height="6" fill="#0f172a" />
                    <rect x="42" y="54" width="6" height="6" fill="#0f172a" />
                    <rect x="52" y="64" width="6" height="6" fill="#0f172a" />
                    <rect x="42" y="74" width="6" height="6" fill="#0f172a" />
                    <rect x="66" y="44" width="6" height="6" fill="#0f172a" />
                    <rect x="76" y="54" width="6" height="6" fill="#0f172a" />
                    <rect x="86" y="64" width="6" height="6" fill="#0f172a" />
                    <rect x="66" y="74" width="6" height="6" fill="#0f172a" />
                    <rect x="76" y="84" width="6" height="6" fill="#0f172a" />
                  </svg>
                </div>
                <span className="ead-qr-caption">Scan Verifikasi Keaslian Kartu</span>
              </div>

              <div className="ead-exam-sig-col">
                <p className="ead-exam-sig-title">Panitia PPDB Sekolah,</p>
                <div className="ead-exam-sig-space">
                  <div className="ead-sig-stamp">
                    PANITIA PPDB<br />PERMATA<br />HARAPAN
                  </div>
                </div>
                <div className="ead-exam-sig-line" />
                <p className="ead-exam-sig-name">{officerName}</p>
                <small className="ead-exam-sig-note">Panitia Penerimaan Peserta Didik Baru</small>
              </div>
            </div>

            {/* Footer */}
            <div className="ead-footer-note" style={{ marginTop: 12 }}>
              Dokumen resmi sistem PPDB Sekolah Permata Harapan. Harap disimpan dengan baik dan dibawa saat tes seleksi berlangsung.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
